import { useState } from 'react';
import { Fragment } from 'react/jsx-runtime';
import { Box, styled } from '@mui/material';

import {
  getShortTextErrorMessage,
  isEmailValid,
  isNumberValid,
  isPhoneNumberValid,
} from '../../helpers';
import { useGetBaseStepFields } from '../../hooks';
import AgathaMessage from '../../Messages/AgathaMessage';
import IrArticleTextMessage from '../../Messages/IrArticleTextMessage';
import UserMessage from '../../Messages/UserMessage';
import { selectIsAuthorIconVisible } from '../../StepWrapper/selectors';
import { useStepContext } from '../../StepWrapper/StepWrapper';
import { ContextVariables, FormFields, FormStepComponent } from '../../types';
import FormWrapper from '../../UtilComponents/FormWrapper/FormWrapper';
import OptionButton from '../../UtilComponents/OptionButton';
import InputComponent from './Fields/InputComponent';

interface FormStepProps {
  isLastStep: boolean;
  setStepContextVariables: (contextVariables: ContextVariables) => void;
  setStepSent: (query?: string) => void;
  stepIndex: number;
  /** Components to display inside the form */
  widgetComponent: FormStepComponent;
}

const FormStep: React.FC<React.PropsWithChildren<FormStepProps>> = ({
  isLastStep,
  setStepContextVariables,
  setStepSent,
  stepIndex,
  widgetComponent,
}) => {
  const context = useStepContext();

  // @Nick TODO: Handle with live chat
  //   const dispatch = useAppDispatch();
  //   const liveChatIntegration = useAppDispatch(selectLiveChatIntegration);
  //   const chatId = useAppSelector(selectChatId);
  //   const isLiveChatMode = selectIsLiveChatMode(context);
  const isAuthorIconVisible = selectIsAuthorIconVisible(context, stepIndex);
  const {
    enabled_feature_flags: featureFlags,
    setTouchedFormFields,
    startNewIntentSelection,
    touchedFormFields,
    updateConversation,
  } = context;

  const shouldHideNeverMindButton = featureFlags.includes(
    'hide_never_mind_button',
  );

  const disableSubmitOnEnter =
    widgetComponent.component_fields.form_fields.findIndex(
      field =>
        field.input_type === 'LONG_TEXT' || field.input_type === 'DROPDOWN',
    ) !== -1;

  const componentFields = widgetComponent.component_fields;
  const componentText = componentFields.forethought_text;
  const { contextVariables, isSent } = useGetBaseStepFields(widgetComponent);
  const formFields = componentFields.form_fields;
  const neverMindSelection = widgetComponent?.neverMindSelection || '';

  const [dropdownLabel, setDropdownLabel] = useState<string>('');

  const validContextVariables = formFields.filter((field: FormFields) => {
    const outputVariable = (
      contextVariables[field.output_variable] ?? ''
    ).toString();

    if (field.input_type === 'EMAIL') {
      return isEmailValid(outputVariable);
    }

    if (field.input_type === 'PHONE_NUMBER' || field.input_type === 'PHONE') {
      return isPhoneNumberValid(outputVariable);
    }

    if (field.input_type === 'NUMBER') {
      return isNumberValid(outputVariable);
    }

    if (field.input_type === 'SHORT_TEXT' && field.configuration_fields) {
      return !Boolean(
        getShortTextErrorMessage(
          outputVariable,
          field.configuration_fields,
          componentText,
        ),
      );
    }

    return outputVariable.trim();
  });

  const isDisabled = formFields.length !== validContextVariables.length;

  const isReroutedByNegativeFeedback =
    !!widgetComponent.isReroutedByNegativeFeedback;

  const inputChange = (
    outputVariable: string,
    value: ContextVariables[string],
  ) => {
    const newState = Object.assign({}, contextVariables);
    newState[outputVariable] = value;
    setStepContextVariables(newState);
    setTouchedFormFields([...touchedFormFields, outputVariable]);
  };

  const onSubmit = (e: React.SyntheticEvent) => {
    e.preventDefault();

    if (isDisabled) {
      setTouchedFormFields(
        formFields.map(({ output_variable }) => output_variable),
      );

      return;
    }

    // @Nick TODO: Handle with live chat
    //   if (isLiveChatMode) {
    //     switch (liveChatIntegration) {
    //       case 'sunco':
    //         Smooch.sendMessage(
    //           {
    //             // @ts-expect-error Smooch types are outdated, `fields` is actually supported
    //             fields: widgetComponent.component_fields.form_fields.map(
    //               ({ output_variable, placeholder }) => ({
    //                 label: placeholder,
    //                 name: output_variable,
    //                 text: contextVariables[output_variable],
    //                 type: 'text',
    //               }),
    //             ),
    //             quotedMessageId: widgetComponent.component_fields.quotedMessageId,
    //             type: 'formResponse',
    //           },
    //           chatId,
    //         );
    //         break;
    //     }

    setStepSent();
    updateConversation(contextVariables);
  };

  const moreThanOneField = formFields.length > 1;

  const isNeverMindButtonSelected = neverMindSelection.length > 0;

  if (isReroutedByNegativeFeedback) {
    return (
      componentFields.prompt && (
        <AgathaMessage
          isAuthorIconVisible={isAuthorIconVisible}
          stepIndex={stepIndex}
        >
          {componentFields.should_render_as_markdown ? (
            <IrArticleTextMessage message={componentFields.prompt} />
          ) : (
            componentFields.prompt
          )}
        </AgathaMessage>
      )
    );
  }

  if (isSent) {
    return (
      <>
        {componentFields.prompt && (
          <AgathaMessage
            agentIcon={componentFields.agentIcon}
            agentName={componentFields.agentName}
            hideTimestamp={widgetComponent.hideTimestamp}
            isAuthorIconVisible={isAuthorIconVisible}
            isLiveChat={widgetComponent.isInAgentConvo}
            stepIndex={stepIndex}
            timestamp={widgetComponent.timestamp}
          >
            {componentFields.should_render_as_markdown ? (
              <IrArticleTextMessage message={componentFields.prompt} />
            ) : (
              componentFields.prompt
            )}
          </AgathaMessage>
        )}

        <UserMessage stepIndex={stepIndex}>
          {isNeverMindButtonSelected
            ? neverMindSelection
            : formFields?.map((component: FormFields, index: number) => {
                const outputVariable = component.output_variable;
                const isDropdown = component.input_type === 'DROPDOWN';
                const isList = component.input_type === 'LIST';
                const outputVariableValue = contextVariables[outputVariable];

                function getFieldValue() {
                  if (isList) {
                    return (
                      component.list_type_options?.find(
                        option => option.value === outputVariableValue,
                      )?.label || ''
                    );
                  }
                  if (isDropdown) {
                    return dropdownLabel;
                  }

                  if (outputVariableValue === true) {
                    return componentText.checkbox_yes;
                  }

                  if (outputVariableValue === false) {
                    return componentText.checkbox_no;
                  }

                  if (component.input_type === 'MULTI_SELECT_LIST') {
                    const options = component.list_type_options || [];
                    const selectedValues = (contextVariables[outputVariable] ||
                      []) as string[];

                    return selectedValues
                      .map(val => {
                        const option = options.find(
                          option => option.value === val,
                        );

                        return option?.label || val;
                      })
                      .join(', ');
                  }

                  return outputVariableValue;
                }

                return (
                  <div key={'componentFields' + index}>
                    {moreThanOneField && (
                      <FieldName isFirstElement={index === 0}>
                        {component.placeholder + ':'}
                      </FieldName>
                    )}
                    <FieldValue>{getFieldValue()}</FieldValue>
                  </div>
                );
              })}
        </UserMessage>
      </>
    );
  }

  return (
    <>
      <FormWrapper
        className='js-form-step'
        disableSubmitOnEnter={disableSubmitOnEnter}
        formTitle={componentFields.prompt || ''}
        isDisabled={false}
        isLastStep={isLastStep}
        onSubmit={onSubmit}
        shouldRenderTitleAsMarkdown={
          !!componentFields.should_render_as_markdown
        }
        stepIndex={stepIndex}
        submitButtonText={componentText.submit_button}
      >
        {formFields?.map((component: FormFields, index: number) => {
          const inputType = component.input_type;
          const outputVariable = component.output_variable;

          return (
            <Fragment key={inputType + index}>
              <Label as={inputType === 'DROPDOWN' ? 'div' : undefined}>
                <InputComponent
                  component={component}
                  componentText={componentText}
                  contextVariables={contextVariables}
                  inputChange={inputChange}
                  inputType={inputType}
                  outputVariable={outputVariable}
                  setDropdownLabel={setDropdownLabel}
                />
              </Label>
            </Fragment>
          );
        })}
      </FormWrapper>
      {/* Do not show "Never mind" button for One Chat: */}
      {!widgetComponent.isInAgentConvo && !shouldHideNeverMindButton && (
        <Box sx={{ paddingX: '8px' }}>
          <OptionButton
            margin='0 auto 16px 0'
            onClick={() => {
              startNewIntentSelection(
                widgetComponent.component_fields.forethought_text.never_mind,
              );
            }}
            text={widgetComponent.component_fields.forethought_text.never_mind}
          />
        </Box>
      )}
    </>
  );
};

export default FormStep;

const FieldName = styled('p')<{ isFirstElement: boolean }>`
  margin: ${props => (props.isFirstElement ? '0' : '16px 0 0;')};
`;

const FieldValue = styled('p')`
  margin: 0;
`;

const Label = styled('label')`
  display: block;
  font-size: inherit;
`;
